import {
  AuditOutlined,
  DownOutlined,
  ClockCircleOutlined,
  CommentOutlined,
  DeleteOutlined,
  EditOutlined,
  LinkOutlined,
  PlusOutlined,
  SearchOutlined,
  SwapOutlined,
  ThunderboltOutlined,
  UpOutlined,
  UserOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import { Input, Select, Tag } from "antd";
import { CSSProperties, FC, useEffect, useMemo, useRef, useState } from "react";
import {
  getReadyTasks,
  layoutTasksByDependencyRows,
} from "../../taskGraph";
import {
  getTaskOverdueContentCount,
  getTaskOverdueDays,
} from "../../taskDue";
import TaskPriorityTag, {
  TASK_PRIORITY_LABELS,
  TaskPriority,
} from "../TaskPriorityTag";
import "./index.css";

type TaskStatus = "todo" | "doing" | "blocked" | "review" | "done" | "discarded";

export interface ProjectTaskMatrixTask {
  id?: string;
  title: string;
  content?: string;
  status: TaskStatus;
  discarded_reason?: string;
  priority?: TaskPriority;
  assignees: string[];
  predecessor_ids: string[];
  due_time?: number;
  created_at?: number;
  comments?: any[];
  subtask_summary: {
    total: number;
    completed: number;
    reviewing: number;
    progress: number;
    overdue_content_count?: number;
    next_content_due_time?: number;
    assignees: string[];
    pending_reviewers: string[];
    items?: Array<{
      id?: string;
      title: string;
      status: "todo" | "transfer" | "review" | "rejected" | "done";
      transferees?: string[];
      reviewers?: string[];
    }>;
  };
}

interface ProjectTaskMatrixViewProps {
  tasks: ProjectTaskMatrixTask[];
  phaseLabel: string;
  currentUserId: string;
  canManage: boolean;
  canComment: boolean;
  getMemberName: (userId: string) => string;
  unreadCommentCounts: Record<string, number>;
  onTaskClick: (task: ProjectTaskMatrixTask) => void;
  onAddRootTask: () => void;
  onAddSuccessor: (task: ProjectTaskMatrixTask) => void;
  onComment: (task: ProjectTaskMatrixTask) => void;
  onEdit: (task: ProjectTaskMatrixTask) => void;
  onDelete: (task: ProjectTaskMatrixTask) => void;
}

const statusText: Record<TaskStatus, string> = {
  todo: "待处理",
  doing: "进行中",
  blocked: "阻塞",
  review: "评审中",
  done: "已完成",
  discarded: "已废弃",
};

const TASK_REGION_WIDTH = 420;

function uniqueValues(values: string[] = []) {
  return Array.from(new Set(values.filter(Boolean)));
}

function formatDate(timestamp?: number) {
  if (!timestamp) return "未设置";
  return new Intl.DateTimeFormat("zh-CN", {
    month: "2-digit",
    day: "2-digit",
  }).format(new Date(timestamp));
}

const ProjectTaskMatrixView: FC<ProjectTaskMatrixViewProps> = ({
  tasks,
  phaseLabel,
  currentUserId,
  canManage,
  canComment,
  getMemberName,
  unreadCommentCounts,
  onTaskClick,
  onAddRootTask,
  onAddSuccessor,
  onComment,
  onEdit,
  onDelete,
}) => {
  const [keyword, setKeyword] = useState("");
  const [statusFilter, setStatusFilter] = useState<TaskStatus | "all">("all");
  const [assigneeFilter, setAssigneeFilter] = useState("all");
  const [priorityFilter, setPriorityFilter] = useState<TaskPriority | "all">(
    "all"
  );
  const [collapsedTaskIds, setCollapsedTaskIds] = useState<Set<string>>(
    () =>
      new Set(
        tasks
          .filter((task) => task.status === "discarded")
          .map((task) => task.id || task.title)
      )
  );
  const previousTaskStatusesRef = useRef<Map<string, TaskStatus>>(new Map());

  useEffect(() => {
    const previousStatuses = previousTaskStatusesRef.current;
    const nextStatuses = new Map<string, TaskStatus>();
    const newlyDiscardedIds: string[] = [];

    tasks.forEach((task) => {
      const taskId = task.id || task.title;
      const previousStatus = previousStatuses.get(taskId);
      nextStatuses.set(taskId, task.status);
      if (task.status === "discarded" && previousStatus !== "discarded") {
        newlyDiscardedIds.push(taskId);
      }
    });

    previousTaskStatusesRef.current = nextStatuses;
    if (newlyDiscardedIds.length) {
      setCollapsedTaskIds((current) => {
        const next = new Set(current);
        newlyDiscardedIds.forEach((taskId) => next.add(taskId));
        return next;
      });
    }
  }, [tasks]);

  const toggleTaskCollapsed = (taskId: string) => {
    setCollapsedTaskIds((current) => {
      const next = new Set(current);
      if (next.has(taskId)) {
        next.delete(taskId);
      } else {
        next.add(taskId);
      }
      return next;
    });
  };

  const taskMap = useMemo(
    () =>
      new Map(
        tasks.filter((task) => task.id).map((task) => [task.id!, task])
      ),
    [tasks]
  );
  const readyTaskIds = useMemo(
    () =>
      new Set(
        getReadyTasks(tasks)
          .map((task) => task.id)
          .filter(Boolean)
      ),
    [tasks]
  );
  const assigneeOptions = useMemo(
    () =>
      uniqueValues(tasks.flatMap((task) => task.assignees)).map((userId) => ({
        label: getMemberName(userId),
        value: userId,
      })),
    [getMemberName, tasks]
  );
  const normalizedKeyword = keyword.trim().toLowerCase();
  const matchesTask = (task: ProjectTaskMatrixTask) => {
    const memberNames = task.assignees.map(getMemberName);
    return (
      (!normalizedKeyword ||
        task.title.toLowerCase().includes(normalizedKeyword) ||
        memberNames.some((name) =>
          name.toLowerCase().includes(normalizedKeyword)
        )) &&
      (statusFilter === "all" || task.status === statusFilter) &&
      (assigneeFilter === "all" || task.assignees.includes(assigneeFilter)) &&
      (priorityFilter === "all" ||
        (task.priority || "medium") === priorityFilter)
    );
  };
  const dependencyLayout = useMemo(
    () => layoutTasksByDependencyRows(tasks),
    [tasks]
  );
  const visibleRegions = dependencyLayout.regions
    .map((region) => ({
      ...region,
      groups: region.groups.map((group) => ({
        ...group,
        totalCount: group.tasks.length,
        tasks: group.tasks
          .map((task, siblingIndex) => ({ siblingIndex, task }))
          .filter(({ task }) => matchesTask(task)),
      })).filter((group) => group.tasks.length > 0),
    }))
    .filter((region) => region.groups.length > 0);
  const filteredCount = visibleRegions.reduce(
    (regionTotal, region) =>
      regionTotal +
      region.groups.reduce(
        (groupTotal, group) => groupTotal + group.tasks.length,
        0
      ),
    0
  );
  const effectiveTasks = tasks.filter((task) => task.status !== "discarded");
  const filteredEffectiveCount = visibleRegions.reduce(
    (regionTotal, region) =>
      regionTotal +
      region.groups.reduce(
        (groupTotal, group) =>
          groupTotal +
          group.tasks.filter(({ task }) => task.status !== "discarded").length,
        0
      ),
    0
  );
  const discardedCount = tasks.length - effectiveTasks.length;
  const overdueCount = effectiveTasks.filter((task) => getTaskOverdueDays(task) > 0)
    .length;
  const mineCount = effectiveTasks.filter((task) =>
    task.assignees.includes(currentUserId)
  ).length;

  return (
    <div className="projectTaskMatrix">
      <div
        className="projectTaskMatrix__controls"
        data-testid="project-task-matrix-controls"
      >
        <div className="projectTaskMatrix__filters">
          <Input
            allowClear
            aria-label="搜索任务名称或负责人"
            onChange={(event) => setKeyword(event.target.value)}
            placeholder="搜索任务"
            prefix={<SearchOutlined />}
          />
          <Select
            onChange={setStatusFilter}
            options={[
              { label: "状态", value: "all" },
              ...Object.entries(statusText).map(([value, label]) => ({
                label,
                value,
              })),
            ]}
            value={statusFilter}
          />
          <Select
            onChange={setAssigneeFilter}
            options={[
              { label: "负责人", value: "all" },
              ...assigneeOptions,
            ]}
            showSearch
            value={assigneeFilter}
          />
          <Select
            onChange={setPriorityFilter}
            options={[
              { label: "优先级", value: "all" },
              ...Object.entries(TASK_PRIORITY_LABELS).map(([value, label]) => ({
                label,
                value,
              })),
            ]}
            value={priorityFilter}
          />
        </div>
        <div className="projectTaskMatrix__summary">
          <Tag className="is-total">
            <span>任务</span>
            <strong>{filteredEffectiveCount}/{effectiveTasks.length}</strong>
          </Tag>
          <Tag className="is-current">
            <span>当前</span>
            <strong>{readyTaskIds.size}</strong>
          </Tag>
          <Tag className="is-overdue">
            <span>逾期</span>
            <strong>{overdueCount}</strong>
          </Tag>
          <Tag className="is-mine">
            <span>我的</span>
            <strong>{mineCount}</strong>
          </Tag>
          {discardedCount > 0 && (
            <Tag className="is-discarded">
              <span>废弃</span>
              <strong>{discardedCount}</strong>
            </Tag>
          )}
        </div>
      </div>

      <div className="projectTaskMatrix__scroll">
        {filteredCount ? (
          <div
            className="projectTaskMatrix__dependencyBoard"
            data-testid="project-task-dependency-board"
            style={
              {
                "--project-task-region-count": Math.max(
                  visibleRegions.length,
                  1
                ),
                "--project-task-region-width": `${TASK_REGION_WIDTH}px`,
              } as CSSProperties
            }
          >
            {visibleRegions.map((region, regionIndex) => (
              <section
                className="projectTaskMatrix__ancestorRegion"
                data-testid={`project-task-ancestor-region-${region.ancestorId}`}
                key={region.ancestorId}
              >
                {regionIndex > 0 && (
                  <span
                    aria-hidden="true"
                    className="projectTaskMatrix__ancestorDivider"
                    data-testid="project-task-ancestor-divider"
                  />
                )}
                {region.groups.map((group) => {
                  const isRootGroup = group.row === 0;
                  const groupLabel =
                    group.totalCount > 1
                      ? `同层任务 · ${group.totalCount} 项`
                      : isRootGroup
                      ? "起点任务"
                      : "后续任务";
                  const groupTestId = `project-task-sibling-group-${region.ancestorId}-${group.row}`;

                  return (
                    <section
                      className={`projectTaskMatrix__siblingGroup ${
                        group.totalCount > 1 ? "is-sibling" : ""
                      }`}
                      data-testid={groupTestId}
                      key={group.groupId}
                    >
                      <header className="projectTaskMatrix__groupHeader">
                        <span>{groupLabel}</span>
                      </header>
                      <div className="projectTaskMatrix__groupTasks">
                        {group.tasks.map(({ siblingIndex, task }) => {
                  const taskId = task.id || task.title;
                  const isDiscarded = task.status === "discarded";
                  const isCollapsed = collapsedTaskIds.has(taskId);
                  const priority = task.priority || "medium";
                  const isCurrent = Boolean(task.id && readyTaskIds.has(task.id));
                  const overdueDays = getTaskOverdueDays(task);
                  const isOverdue = overdueDays > 0;
                  const assignees = uniqueValues(task.assignees).sort(
                    (previous, next) =>
                      Number(next === currentUserId) -
                      Number(previous === currentUserId)
                  );
                  const predecessorTasks = (task.predecessor_ids || [])
                    .map((predecessorId) => taskMap.get(predecessorId))
                    .filter(Boolean) as ProjectTaskMatrixTask[];
                  const subtaskSummary = task.subtask_summary;
                  const subtaskItems = subtaskSummary.items || [];
                  const overdueSubtaskContentCount =
                    getTaskOverdueContentCount(task);
                  const hasOverdueSubtaskContent = overdueSubtaskContentCount > 0;
                  const needsMyReview =
                    !isDiscarded &&
                    subtaskSummary.pending_reviewers.includes(currentUserId);
                  const myPendingTransferReviewCount = isDiscarded ? 0 : subtaskItems.filter(
                    (subtask) =>
                      (subtask.status === "transfer" ||
                        subtask.status === "rejected") &&
                      (subtask.transferees || []).includes(currentUserId) &&
                      Boolean(subtask.reviewers?.length)
                  ).length;
                  const myPendingTransferCompleteCount = isDiscarded ? 0 : subtaskItems.filter(
                    (subtask) =>
                      subtask.status === "transfer" &&
                      (subtask.transferees || []).includes(currentUserId) &&
                      !subtask.reviewers?.length
                  ).length;
                  const hasMyPendingTransferAction =
                    myPendingTransferReviewCount > 0 ||
                    myPendingTransferCompleteCount > 0;
                  const commentCount = task.comments?.length || 0;
                  const unreadCount = unreadCommentCounts[taskId] || 0;

                  return (
                    <article
                      aria-label={`查看任务详情 ${task.title}`}
                      className={`projectTaskMatrixCard projectTaskMatrixCard--${task.status} projectTaskMatrixCard--priority-${priority} ${
                        isCurrent ? "projectTaskMatrixCard--current" : ""
                      } ${isOverdue ? "projectTaskMatrixCard--overdue" : ""} ${
                        isCurrent && isOverdue
                          ? "projectTaskMatrixCard--overdueCurrent"
                          : ""
                      } ${
                        !isDiscarded && subtaskSummary.reviewing > 0
                          ? "projectTaskMatrixCard--hasReview"
                          : ""
                      } ${
                        hasMyPendingTransferAction
                          ? "projectTaskMatrixCard--hasTransferAction"
                          : ""
                      } ${
                        hasOverdueSubtaskContent
                          ? "projectTaskMatrixCard--hasOverdueSubtaskContent"
                          : ""
                      } ${
                        isCollapsed ? "projectTaskMatrixCard--collapsed" : ""
                      }`}
                      data-testid={`project-task-card-${taskId}`}
                      key={taskId}
                      onClick={() => onTaskClick(task)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          onTaskClick(task);
                        }
                      }}
                      role="button"
                      tabIndex={0}
                    >
                      {isCurrent && (
                        <>
                          <div className="projectTaskMatrixCard__borderPulse">
                            <span /><span /><span /><span />
                          </div>
                          <div className="projectTaskMatrixCard__currentBadge">
                            <ThunderboltOutlined />
                            当前进行项
                          </div>
                        </>
                      )}
                      <div
                        className={`projectTaskMatrixCard__status projectTaskMatrixCard__status--${task.status} ${
                          isOverdue
                            ? "projectTaskMatrixCard__status--overdue"
                            : ""
                        }`}
                      >
                        <span>
                          {isOverdue ? <WarningOutlined /> : <i />}
                          {isOverdue ? `逾期 ${overdueDays} 天` : statusText[task.status]}
                        </span>
                        <div className="projectTaskMatrixCard__statusActions">
                          <TaskPriorityTag priority={priority} />
                          <button
                            aria-expanded={!isCollapsed}
                            aria-label={`${
                              isCollapsed ? "展开" : "收起"
                            }任务模块 ${task.title}`}
                            className="projectTaskMatrixCard__collapseIcon"
                            onClick={(event) => {
                              event.stopPropagation();
                              toggleTaskCollapsed(taskId);
                            }}
                            title={isCollapsed ? "展开任务模块" : "收起任务模块"}
                            type="button"
                          >
                            {isCollapsed ? <DownOutlined /> : <UpOutlined />}
                          </button>
                        </div>
                      </div>
                      <div className="projectTaskMatrixCard__body">
                        <div className="projectTaskMatrixCard__open">
                          <strong title={task.title}>{task.title}</strong>
                          <div className="projectTaskMatrixCard__mySignals">
                            {myPendingTransferReviewCount > 0 ? (
                              <span className="projectTaskMatrixCard__transferSignal">
                                <SwapOutlined />
                                待你提交审批
                                {myPendingTransferReviewCount > 1 && (
                                  <b>{myPendingTransferReviewCount}</b>
                                )}
                              </span>
                            ) : myPendingTransferCompleteCount > 0 ? (
                              <span className="projectTaskMatrixCard__transferSignal is-complete">
                                <SwapOutlined />
                                待你确认完成
                                {myPendingTransferCompleteCount > 1 && (
                                  <b>{myPendingTransferCompleteCount}</b>
                                )}
                              </span>
                            ) : null}
                            {task.assignees.includes(currentUserId) && (
                              <span className="projectTaskMatrixCard__selfSignal">
                                <UserOutlined />我负责
                              </span>
                            )}
                          </div>
                        </div>
                        {isCollapsed ? (
                          <div className="projectTaskMatrixCard__collapsedSummary">
                            <span>
                              {isDiscarded
                                ? task.discarded_reason || "该任务已废弃"
                                : `${assignees.length} 名相关成员`}
                            </span>
                            <small>
                              {isDiscarded
                                ? "已停止统计"
                                : `${subtaskSummary.completed} / ${subtaskSummary.total} 子任务`}
                            </small>
                          </div>
                        ) : (
                          <>
                        <div
                          className="projectTaskMatrixCard__relations"
                          data-testid={`project-task-relations-${taskId}`}
                        >
                          {assignees.map((assigneeId) => {
                            const isSelf = assigneeId === currentUserId;
                            const memberName = getMemberName(assigneeId);
                            return (
                              <Tag
                                className={
                                  isSelf
                                    ? "projectTaskMatrixCard__assignee--self"
                                    : ""
                                }
                                key={assigneeId}
                                title={memberName}
                              >
                                {isSelf && <UserOutlined />}
                                {memberName}{isSelf ? "（我）" : ""}
                              </Tag>
                            );
                          })}
                          {predecessorTasks.map((predecessor) => (
                            <Tag
                              className="projectTaskMatrixCard__predecessorTag"
                              icon={<LinkOutlined />}
                              key={predecessor.id || predecessor.title}
                              title={`前置任务：${predecessor.title}`}
                            >
                              前置 · {predecessor.title}
                            </Tag>
                          ))}
                          {group.totalCount > 1 && (
                            <Tag className="projectTaskMatrixCard__siblingTag">
                              同级 {siblingIndex + 1} / {group.totalCount}
                            </Tag>
                          )}
                        </div>
                        <div className="projectTaskMatrixCard__footer">
                          <div className="projectTaskMatrixCard__metaRow">
                            <span className={isOverdue ? "is-overdue" : ""}>
                              <ClockCircleOutlined /> {formatDate(task.due_time)}
                            </span>
                            {commentCount > 0 && (
                              <span
                                className={`projectTaskMatrixCard__commentSummary ${
                                  unreadCount > 0 ? "is-unread" : ""
                                }`}
                              >
                                <CommentOutlined />
                                <b>批注 {commentCount}</b>
                                {unreadCount > 0 && <em>{unreadCount} 未读</em>}
                              </span>
                            )}
                          </div>
                          <div className="projectTaskMatrixCard__subtaskProgress">
                            {isDiscarded ? (
                              <div className="projectTaskMatrixCard__discardedNotice">
                                <strong>已停止统计</strong>
                                <span title={task.discarded_reason || ""}>
                                  {task.discarded_reason || "该任务及其子任务已废弃"}
                                </span>
                              </div>
                            ) : subtaskSummary.total ? (
                              <>
                                <div className="projectTaskMatrixCard__subtaskHeader">
                                  <strong>{subtaskSummary.completed} / {subtaskSummary.total} 子任务</strong>
                                  <span>{subtaskSummary.progress}%</span>
                                </div>
                                {!isDiscarded && subtaskSummary.reviewing > 0 && (
                                  <div
                                    className={`projectTaskMatrixCard__reviewNotice ${
                                      needsMyReview ? "is-mine" : ""
                                    }`}
                                  >
                                    <AuditOutlined />
                                    {needsMyReview
                                      ? "待你审核"
                                      : `${subtaskSummary.reviewing} 个待审批`}
                                  </div>
                                )}
                                {hasOverdueSubtaskContent && (
                                  <div className="projectTaskMatrixCard__subtaskOverdueNotice">
                                    <WarningOutlined />
                                    <span>{overdueSubtaskContentCount} 项内容逾期</span>
                                    {subtaskSummary.next_content_due_time ? (
                                      <small>
                                        最近截止 {formatDate(subtaskSummary.next_content_due_time)}
                                      </small>
                                    ) : null}
                                  </div>
                                )}
                                <ul
                                  aria-label={`${task.title}的子任务标题`}
                                  className="projectTaskMatrixCard__subtaskList"
                                  style={
                                    {
                                      "--project-task-visible-subtask-rows": 3,
                                    } as CSSProperties
                                  }
                                >
                                  {subtaskItems.map((subtask, index) => (
                                    <li
                                      className={`is-${subtask.status}`}
                                      key={subtask.id || `${taskId}-${index}`}
                                    >
                                      <i />
                                      <span
                                        className={`is-${subtask.status}`}
                                        title={subtask.title}
                                      >
                                        {subtask.title}
                                      </span>
                                    </li>
                                  ))}
                                </ul>
                                <i className="projectTaskMatrixCard__progressTrack">
                                  <b style={{ width: `${subtaskSummary.progress}%` }} />
                                </i>
                              </>
                            ) : (
                              <strong className="is-empty projectTaskMatrixCard__subtaskEmpty">
                                未拆分 · 0 个子任务 · 进度 0%
                              </strong>
                            )}
                          </div>
                          <div className="projectTaskMatrixCard__actions">
                            {canManage && !isDiscarded && (
                              <button
                                aria-label={`为 ${task.title} 新增后续任务`}
                                onClick={(event) => {
                                  event.stopPropagation();
                                  onAddSuccessor(task);
                                }}
                                title="新增后续任务"
                                type="button"
                              ><PlusOutlined /></button>
                            )}
                            {canComment && !isDiscarded && (
                              <button
                                aria-label={`批注 ${task.title}`}
                                className="is-comment"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  onComment(task);
                                }}
                                title="新增批注"
                                type="button"
                              ><CommentOutlined /></button>
                            )}
                            {canManage && (
                              <>
                                <button
                                  aria-label={`编辑 ${task.title}`}
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    onEdit(task);
                                  }}
                                  title={isDiscarded ? "恢复任务" : "编辑任务"}
                                  type="button"
                                ><EditOutlined /></button>
                                <button
                                  aria-label={`删除 ${task.title}`}
                                  className="is-delete"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    onDelete(task);
                                  }}
                                  title="删除任务"
                                  type="button"
                                ><DeleteOutlined /></button>
                              </>
                            )}
                          </div>
                        </div>
                          </>
                        )}
                      </div>
                    </article>
                  );
                        })}
                      </div>
                    </section>
                  );
                })}
              </section>
            ))}
          </div>
        ) : (
          <div className="projectTaskMatrix__empty">
            <strong>{tasks.length ? "没有符合筛选条件的任务" : `${phaseLabel}暂无任务`}</strong>
            <span>{tasks.length ? "请调整筛选条件" : "新增任务后会按前置关系逐行向下排列"}</span>
            {canManage && !tasks.length && (
              <button onClick={onAddRootTask} type="button">
                <PlusOutlined /> 新增首个任务
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProjectTaskMatrixView;
